// SAD-500 (SOL-04): early staffing and LOE. An early estimate beside the Solution,
// never formal pricing: nothing here reads or writes a pricing record. The total
// is the sum over priced lines; an unpriced line is never $0 and makes it partial.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton } from '@mono/ui-components';
import { SpecraInlineBanner, SpecraRestrictedChip } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { deliveryClient, projectDataClient } from '../../../lib/connect/clients';
import { useTenantId } from '../../Delivery/_shared/hooks/useTenantId';
import { contactHooks } from '../../Delivery/Registries/hooks';
import { Choice, TextField } from '../PastPerformance/Fields';
const usd = (cents)=>`$${(Number(cents) / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
const amountOf = (hoursCenti, rate)=>(hoursCenti * rate + BigInt(50)) / BigInt(100);
const hoursOf = (centi)=>Number(centi) / 100;
const toDraft = (l)=>({
        subjectKind: l.subjectKind,
        subjectRef: l.subjectRef,
        subjectLabel: l.subjectLabel,
        hoursCenti: l.hoursCenti,
        rateCents: l.rateCents,
        rateSource: l.rateSource,
        rateObservationId: l.rateObservationId,
        rateSourceLabel: l.rateSourceLabel,
        rateRestricted: l.rateRestricted
    });
function LineRow({ line }) {
    const rate = line.rateCents;
    return /*#__PURE__*/ _jsxs("li", {
        "data-testid": Testids.captureStaffing.lineRow,
        className: "flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm",
        children: [
            /*#__PURE__*/ _jsx(SpecraBadge, {
                children: line.subjectKind
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "font-medium",
                children: line.subjectLabel
            }),
            /*#__PURE__*/ _jsxs("span", {
                children: [
                    hoursOf(line.hoursCenti),
                    " h"
                ]
            }),
            line.rateRestricted ? /*#__PURE__*/ _jsx(SpecraRestrictedChip, {
                label: "Comp vault",
                reason: "Person-level rates are visible only to comp-vault grantees for this legal entity."
            }) : rate !== undefined ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        children: [
                            "@ ",
                            usd(rate),
                            "/h (",
                            line.rateSource === 'manual' ? 'manual rate' : line.rateSourceLabel || 'rate observation',
                            ")"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        "data-testid": Testids.captureStaffing.lineAmount,
                        children: usd(amountOf(line.hoursCenti, rate))
                    })
                ]
            }) : /*#__PURE__*/ _jsx("span", {
                "data-testid": Testids.captureStaffing.lineUnpriced,
                children: /*#__PURE__*/ _jsx(SpecraBadge, {
                    children: "Unpriced — not counted as $0"
                })
            })
        ]
    });
}
function Summary({ revision }) {
    const roleOnly = revision.totalScope === 'role_lines_only';
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "text-sm",
                children: [
                    roleOnly ? 'LOE total (role lines)' : 'Early LOE estimate (priced lines only)',
                    ":",
                    ' ',
                    /*#__PURE__*/ _jsx("strong", {
                        "data-testid": Testids.captureStaffing.total,
                        children: usd(revision.loeTotalCents)
                    }),
                    ' ',
                    "USD"
                ]
            }),
            roleOnly && /*#__PURE__*/ _jsxs("p", {
                className: "text-sm text-muted-foreground",
                children: [
                    revision.restrictedPersonLines ?? 0,
                    " person lines priced, restricted"
                ]
            }),
            revision.isPartial ? /*#__PURE__*/ _jsxs("p", {
                "data-testid": Testids.captureStaffing.partialFlag,
                className: "text-sm font-semibold",
                children: [
                    "PARTIAL: ",
                    revision.unpricedLines,
                    " unpriced line(s) are not included; this total is a lower bound."
                ]
            }) : /*#__PURE__*/ _jsxs("p", {
                className: "text-sm",
                children: [
                    "Complete: all ",
                    revision.pricedLines,
                    " line(s) are priced."
                ]
            })
        ]
    });
}
function Editor({ head, captureProjectId }) {
    const queryClient = useQueryClient();
    const tenant = useTenantId();
    const [lines, setLines] = useState((head?.lines ?? []).map(toDraft));
    const [kind, setKind] = useState('person');
    const [person, setPerson] = useState('');
    const [role, setRole] = useState('');
    const [hours, setHours] = useState('');
    const [rateChoice, setRateChoice] = useState('unpriced');
    const [manual, setManual] = useState('');
    const [changeNote, setChangeNote] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const contacts = contactHooks.useList({
        companyId: tenant
    }, {
        enabled: !!tenant
    });
    const observations = useQuery({
        queryKey: [
            'capture-staffing-rates',
            tenant
        ],
        enabled: !!tenant,
        queryFn: async ()=>(await deliveryClient.listRateObservations({
                companyId: tenant,
                limit: 100
            })).observations.filter((o)=>o.rateUnit === 'HOURLY' && o.currency === 'USD' && !o.invalidatedAt)
    });
    const people = contacts.data?.items ?? [];
    const obs = observations.data ?? [];
    const hoursNum = Number(hours);
    const manualNum = Number(manual);
    const noPersonRates = head?.totalScope === 'role_lines_only';
    const subjectOk = kind === 'person' ? person !== '' : role.trim() !== '';
    const hideRate = kind === 'person' && noPersonRates;
    const rateOk = hideRate || rateChoice !== 'manual' || manual !== '' && manualNum >= 0;
    const canAdd = subjectOk && hoursNum > 0 && rateOk;
    function add() {
        const p = people.find((c)=>c.id === person);
        const o = obs.find((r)=>r.id === rateChoice);
        const d = {
            subjectKind: kind,
            subjectRef: kind === 'person' ? person : '',
            subjectLabel: kind === 'person' ? `${p?.firstName ?? ''} ${p?.lastName ?? ''}`.trim() || person : role.trim(),
            hoursCenti: BigInt(Math.round(hoursNum * 100)),
            rateSource: '',
            rateObservationId: '',
            rateSourceLabel: ''
        };
        if (hideRate) {
        // person rates are never set without comp-vault access
        } else if (rateChoice === 'manual') {
            d.rateCents = BigInt(Math.round(manualNum * 100));
            d.rateSource = 'manual';
        } else if (o) {
            d.rateCents = BigInt(Math.round(o.amount * 100));
            d.rateSource = 'observation';
            d.rateObservationId = o.id;
            d.rateSourceLabel = `${o.lcatTitle || 'Rate book'} (${o.observationType.toLowerCase()})`;
        }
        setLines((ls)=>[
                ...ls,
                d
            ]);
        setHours('');
        setManual('');
        setRole('');
        setRateChoice('unpriced');
    }
    async function save() {
        setBusy(true);
        setError('');
        try {
            await projectDataClient.saveCaptureStaffingRevision({
                captureProjectId,
                requestId: crypto.randomUUID(),
                expectedRevision: head?.revision ?? BigInt(0),
                changeNote,
                lines
            });
            setChangeNote('');
            await queryClient.invalidateQueries({
                queryKey: [
                    'capture-staffing',
                    captureProjectId
                ]
            });
        } catch (e) {
            const ce = ConnectError.from(e);
            setError(ce.code === Code.Aborted ? 'Someone saved a newer staffing revision. Reload and reapply your edit.' : ce.rawMessage);
        } finally{
            setBusy(false);
        }
    }
    const draftTotal = lines.reduce((s, l)=>l.rateCents === undefined ? s : s + (l.hoursCenti * l.rateCents + BigInt(50)) / BigInt(100), BigInt(0));
    const restrictedCount = lines.filter((l)=>l.rateRestricted).length;
    const unpriced = lines.filter((l)=>l.rateCents === undefined && !l.rateRestricted).length;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-2",
                children: lines.map((l, i)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(LineRow, {
                                line: l
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.captureStaffing.removeLine,
                                onClick: ()=>setLines((ls)=>ls.filter((_, j)=>j !== i)),
                                children: "Remove"
                            })
                        ]
                    }, i))
            }),
            lines.length > 0 && /*#__PURE__*/ _jsx(Summary, {
                revision: {
                    loeTotalCents: draftTotal,
                    pricedLines: lines.filter((l)=>l.rateCents !== undefined).length,
                    unpricedLines: unpriced,
                    isPartial: unpriced > 0 || restrictedCount > 0,
                    totalScope: restrictedCount > 0 ? 'role_lines_only' : 'all',
                    restrictedPersonLines: restrictedCount
                }
            }),
            /*#__PURE__*/ _jsx(Choice, {
                label: "Staffed by",
                testId: Testids.captureStaffing.kind,
                value: kind,
                options: [
                    {
                        value: 'person',
                        label: 'Canonical person'
                    },
                    {
                        value: 'role',
                        label: 'Role / LCAT placeholder'
                    }
                ],
                onChange: setKind
            }),
            kind === 'person' ? /*#__PURE__*/ _jsx(Choice, {
                label: "Person",
                testId: Testids.captureStaffing.person,
                value: person,
                options: [
                    {
                        value: '',
                        label: 'Select a person…'
                    },
                    ...people.map((c)=>({
                            value: c.id,
                            label: `${c.firstName} ${c.lastName}`.trim()
                        }))
                ],
                onChange: setPerson
            }) : /*#__PURE__*/ _jsx(TextField, {
                label: "Role or LCAT",
                testId: Testids.captureStaffing.role,
                value: role,
                onChange: setRole
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "Hours",
                testId: Testids.captureStaffing.hours,
                value: hours,
                onChange: setHours
            }),
            hideRate ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "neutral",
                children: "Person-level rates need comp-vault access. Add this person's role as a role line to price it."
            }) : /*#__PURE__*/ _jsx(Choice, {
                label: "Rate",
                testId: Testids.captureStaffing.rateChoice,
                value: rateChoice,
                options: [
                    {
                        value: 'unpriced',
                        label: 'Unpriced (no rate selected)'
                    },
                    {
                        value: 'manual',
                        label: 'Enter a manual rate'
                    },
                    ...obs.map((o)=>({
                            value: o.id,
                            label: `Rate book: ${o.lcatTitle || o.id} ${o.observationType.toLowerCase()} $${o.amount.toFixed(2)}/h`
                        }))
                ],
                onChange: setRateChoice
            }),
            rateChoice === 'manual' && !hideRate && /*#__PURE__*/ _jsx(TextField, {
                label: "Manual rate (USD per hour)",
                testId: Testids.captureStaffing.manualRate,
                value: manual,
                onChange: setManual
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureStaffing.addLine,
                disabled: !canAdd,
                onClick: add,
                children: "Add staffing line"
            }),
            /*#__PURE__*/ _jsx(TextField, {
                label: "What changed (optional)",
                testId: Testids.captureStaffing.changeNote,
                value: changeNote,
                onChange: setChangeNote
            }),
            error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                "data-testid": Testids.captureStaffing.error,
                children: error
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.captureStaffing.save,
                disabled: busy || lines.length === 0,
                onClick: save,
                children: "Save staffing revision"
            })
        ]
    });
}
export function StaffingLoe({ captureProjectId }) {
    const [viewing, setViewing] = useState();
    const staffing = useQuery({
        queryKey: [
            'capture-staffing',
            captureProjectId
        ],
        queryFn: async ()=>(await projectDataClient.getCaptureStaffing({
                captureProjectId
            })).revisions,
        retry: false
    });
    if (staffing.isLoading) return /*#__PURE__*/ _jsx("p", {
        children: "Loading staffing…"
    });
    if (staffing.isError) return /*#__PURE__*/ _jsx("p", {
        role: "alert",
        children: ConnectError.from(staffing.error).rawMessage
    });
    const revisions = staffing.data ?? [];
    const head = revisions[0];
    const viewed = revisions.find((r)=>r.revision === viewing);
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": Testids.captureStaffing.root,
        className: "space-y-3 rounded-md border p-3",
        children: [
            /*#__PURE__*/ _jsx("h3", {
                className: "font-semibold",
                children: "Early staffing and LOE"
            }),
            /*#__PURE__*/ _jsx("p", {
                "data-testid": Testids.captureStaffing.banner,
                className: "text-sm",
                children: "Early estimate only. This is not formal pricing and writes no pricing record; formal pricing is built in the pricing workspace."
            }),
            viewed ? /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsxs("h4", {
                        className: "font-medium",
                        children: [
                            "Revision ",
                            String(viewed.revision),
                            " (read-only)"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-2",
                        children: viewed.lines.map((l)=>/*#__PURE__*/ _jsx(LineRow, {
                                line: l
                            }, l.id))
                    }),
                    /*#__PURE__*/ _jsx(Summary, {
                        revision: viewed
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.captureStaffing.closeViewing,
                        onClick: ()=>setViewing(undefined),
                        children: "Back to working version"
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        "data-testid": Testids.captureStaffing.headRevision,
                        className: "font-medium",
                        children: head ? `Working version: revision ${String(head.revision)}` : 'No staffing revision yet'
                    }),
                    /*#__PURE__*/ _jsx(Editor, {
                        head: head,
                        captureProjectId: captureProjectId
                    }, head?.id ?? 'new')
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1 text-sm",
                children: revisions.map((r)=>/*#__PURE__*/ _jsxs("li", {
                        "data-testid": Testids.captureStaffing.historyRow,
                        className: "flex flex-wrap items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                className: "font-medium",
                                children: [
                                    "Revision ",
                                    String(r.revision)
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    usd(r.loeTotalCents),
                                    r.totalScope === 'role_lines_only' ? ' (role lines)' : ''
                                ]
                            }),
                            r.isPartial && /*#__PURE__*/ _jsx(SpecraBadge, {
                                children: "partial"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: r.changeNote || 'No note'
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.captureStaffing.viewRevision,
                                onClick: ()=>setViewing(r.revision),
                                children: "View"
                            })
                        ]
                    }, r.id))
            })
        ]
    });
}
